import type { VoidFunctionComponent } from 'react';

import { useFossabotPRCounts } from '../../../hooks/api/fossabot/useFossabotPRCounts';
import Subnav from '../../generic/Subnav';
import SubnavCountBadge from '../../generic/Subnav/SubnavCountBadge';

/** Hover text on the badge once the counts request has failed. */
const UNAVAILABLE_TITLE = 'PR counts unavailable';

type Props = {
  /**
   * The URL including the branch/revision part. The list covers every revision,
   * but the route sits under the revision container like the other project tabs.
   * @example `/projects/custom%2B17%2Fcool-project/refs/branch/main/123`
   */
  baseRevisionURL: string;
  /**
   * The project locator, without the revision ID.
   * @example `custom+17/cool-project`
   */
  projectLocator: string;
};

/**
 * The "PRs" subnav item, with a badge showing how many PRs fossabot has opened
 * for the project. It fetches its own count, like the other counted items, so
 * the rest of the subnav does not re-render when the count lands. The page's
 * stat cards share that request: both go through the same query key.
 */
export const ProjectSubnavItemFossabotPrs: VoidFunctionComponent<Props> = ({
  baseRevisionURL,
  projectLocator,
}) => {
  const { data: counts, isLoading, isError } = useFossabotPRCounts(projectLocator);

  /*
   * Subnav.ItemWithCount does not forward the badge's unavailable state, so this
   * pairs Subnav.Item with the badge directly, the way the Issues item does.
   */
  return (
    <Subnav.Item to={`${baseRevisionURL}/fossabot-prs`} navKey="fossabot-prs">
      PRs
      <SubnavCountBadge
        count={counts?.total ?? 0}
        isLoading={isLoading}
        isUnavailable={isError}
        title={isError ? UNAVAILABLE_TITLE : undefined}
      />
      {isError && <span className="sr-only">{UNAVAILABLE_TITLE}</span>}
    </Subnav.Item>
  );
};
